/**
 * Parse, check and normalise FlexiCo templates uploaded as JSON, applying the same rules
 * as the manual form, then build the payload the form sends to createFlexicoTemplate.
 * Pure functions: the Glue job check is injected so it can be tested.
 */
import { FLEXICO_STEP_FUNCTION_CONFIG } from 'src/constants/AppConstants';
import { FLEXICO_TEMPLATE_STATUS } from '../Constants';
import {
    AUTO_SET_PARAMETER_KEYS, AUTO_SET_TEMPLATE_KEYS, APPROVER_TYPE_VALUES, CONTICO_JOB_LABEL, CUSTOM_ATTRIBUTE_TYPES,
    DYNAMIC_TYPES, NO_APPROVAL_TRIGGER_TYPES, NOTIFICATION_PREFERENCES, PARAMETER_FIELDS, PARAMETER_TYPES, SOURCE_DATABASES,
    TEMPLATE_FIELDS, UI_ONLY_ATTRIBUTE_TYPES, UPLOAD_TRIGGER_TYPES, UPLOAD_WITH_PARAMETERS, UPLOADER_TYPE_VALUES, USER_ENTERED_TYPES,
} from './flexicoTemplateSpec';

const TEMPLATE_KEYS = new Set(TEMPLATE_FIELDS.map((f) => f.key));
const PARAMETER_KEYS = new Set(PARAMETER_FIELDS.map((f) => f.key));
const BOOLEAN_TEMPLATE_KEYS = ['upload_mandatory', 'critical_data', 'sox_approved', 'approval_required'];
const BOOLEAN_PARAMETER_KEYS = ['is_mandatory', 'is_hidden', 'limit_permissions'];
const MAX_TEMPLATES = 50;

const isObject = (v) => v !== null && typeof v === 'object' && !Array.isArray(v);
const isBlank = (v) => typeof v !== 'string' || v.trim() === '';
// The form strips the corporate domain from aliases.
const cleanAlias = (alias) => alias.replace(/@amazon\.com$/i, '').trim();
// null means "not set" (exports from Download App use null for empty fields).
const withoutNulls = (obj) => Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== null));

/** Accepts {"templates": [...]}, a bare array, or a single template object. */
export const parseTemplatesFile = (text) => {
    let data;
    try {
        data = JSON.parse(text);
    } catch (e) {
        return { templates: [], error: `The file is not valid JSON: ${e.message}` };
    }
    const templates = Array.isArray(data) ? data
        : isObject(data) && Array.isArray(data.templates) ? data.templates
        : isObject(data) ? [data] : null;
    if (!templates) return { templates: [], error: 'The file must contain a template, a list of templates, or { "templates": [...] }.' };
    if (templates.length === 0) return { templates: [], error: 'The file does not contain any templates.' };
    if (templates.length > MAX_TEMPLATES) return { templates: [], error: `A file can contain at most ${MAX_TEMPLATES} templates.` };
    return { templates, error: null };
};

const checkPeople = (list, where, allowedTypes, errors) => {
    if (!Array.isArray(list)) { errors.push(`${where} must be a list.`); return []; }
    const seen = new Set();
    return list.map((p, i) => {
        if (!isObject(p) || isBlank(p.alias)) { errors.push(`${where}[${i}] needs an alias.`); return p; }
        const person = { ...p, alias: cleanAlias(p.alias) };
        if (!allowedTypes.includes(person.type)) errors.push(`${where}[${i}] type must be one of ${allowedTypes.join(', ')}.`);
        const key = `${person.type}|${person.alias}`;
        if (seen.has(key)) errors.push(`${where} lists ${person.alias} more than once.`);
        seen.add(key);
        return person;
    });
};

const normaliseInfo = (info, where, errors) => {
    let value = info;
    if (typeof value === 'string') {
        try { value = JSON.parse(value); } catch (e) { errors.push(`${where}.info must be { "description": "…" } or { "url": "…" }.`); return undefined; }
    }
    if (!isObject(value) || Object.keys(value).length !== 1 || !('description' in value || 'url' in value)) {
        errors.push(`${where}.info must be either { "description": "…" } or { "url": "…" }.`);
        return undefined;
    }
    if ('description' in value && isBlank(value.description)) errors.push(`${where}.info.description must not be empty.`);
    if ('url' in value && (isBlank(value.url) || !/^https?:\/\//i.test(value.url.trim()))) errors.push(`${where}.info.url must start with http:// or https://.`);
    return JSON.stringify('url' in value ? { url: String(value.url).trim() } : { description: value.description });
};

const normaliseParameter = (raw, index, errors, warnings) => {
    const where = `parameters[${index}]${raw && typeof raw.label === 'string' ? ` (${raw.label})` : ''}`;
    if (!isObject(raw)) { errors.push(`parameters[${index}] must be an object.`); return null; }
    const p = withoutNulls(raw);
    AUTO_SET_PARAMETER_KEYS.forEach((k) => { if (k in p) { delete p[k]; warnings.push(`${where}: "${k}" is assigned automatically and was ignored.`); } });
    Object.keys(p).filter((k) => !PARAMETER_KEYS.has(k)).forEach((k) => errors.push(`${where}: unknown field "${k}".`));

    if (isBlank(p.label)) errors.push(`parameters[${index}] needs a label.`); else p.label = p.label.trim();
    if (!(p.field_type in PARAMETER_TYPES)) {
        errors.push(`${where}: field_type must be one of ${Object.keys(PARAMETER_TYPES).join(', ')}.`);
        return p;
    }
    BOOLEAN_PARAMETER_KEYS.forEach((k) => { if (k in p && typeof p[k] !== 'boolean') errors.push(`${where}: ${k} must be true or false.`); });

    if (p.field_values === undefined || p.field_values === null) p.field_values = [];
    if (!Array.isArray(p.field_values) || p.field_values.some((v) => typeof v !== 'string' && typeof v !== 'number')) {
        errors.push(`${where}: field_values must be a list of strings.`);
        return p;
    }
    p.field_values = p.field_values.map(String);

    const values = p.field_values;
    const type = p.field_type;
    if (USER_ENTERED_TYPES.includes(type)) {
        p.field_values = [' '];
    } else if (type === 'fixed_inputs' && values.filter((v) => v.trim()).length === 0) {
        errors.push(`${where}: fixed_inputs needs at least one value.`);
    } else if (type === 'nested_checkbox_group' && values.filter((v) => v.trim()).length === 0) {
        errors.push(`${where}: nested_checkbox_group needs at least one child option.`);
    } else if (type === 'numeric_up_down' && (values.length > 1 || (values[0] !== undefined && !/^\d+$/.test(values[0])))) {
        errors.push(`${where}: numeric_up_down takes at most one value, digits only.`);
    } else if (DYNAMIC_TYPES.includes(type) && (values.length !== 1 || isBlank(values[0]))) {
        errors.push(`${where}: ${type} needs exactly one value, the SQL query.`);
    } else if (type === 'tree') {
        let ok = values.length === 1;
        try { ok = ok && Array.isArray(JSON.parse(values[0])); } catch (e) { ok = false; }
        if (!ok) errors.push(`${where}: tree needs exactly one value, a JSON list of { "label", "value", "children" }.`);
    } else if (['select', 'multi_select', 'radio_group', 'checkbox_group'].includes(type) && values.length === 0) {
        warnings.push(`${where}: ${type} has no options.`);
    }

    if (DYNAMIC_TYPES.includes(type)) {
        if (p.source_database === undefined) p.source_database = SOURCE_DATABASES[0];
        else if (!SOURCE_DATABASES.includes(p.source_database)) errors.push(`${where}: source_database must be ${SOURCE_DATABASES.join(' or ')}.`);
    } else if ('source_database' in p) {
        delete p.source_database;
        warnings.push(`${where}: source_database is only used by dynamic types and was ignored.`);
    }

    if (p.is_mandatory && p.limit_permissions) errors.push(`${where}: is_mandatory and limit_permissions cannot both be true.`);
    if ('permissions' in p) {
        if (!p.limit_permissions) { delete p.permissions; warnings.push(`${where}: permissions are only used with limit_permissions and were ignored.`); }
        else p.permissions = checkPeople(p.permissions, `${where}.permissions`, UPLOADER_TYPE_VALUES, errors);
    }
    if ('info' in p) {
        const info = normaliseInfo(p.info, where, errors);
        if (info === undefined) delete p.info; else p.info = info;
    }
    if (p.display_index === undefined || p.display_index === '') p.display_index = String(index);
    else if (!/^\d+$/.test(String(p.display_index))) errors.push(`${where}: display_index must be a whole number.`);
    else p.display_index = String(p.display_index);
    if (p.display_name === undefined && p.label) p.display_name = p.label;
    return p;
};

const normaliseCustomAttributes = (list, parameterLabels, errors, warnings) => {
    if (!Array.isArray(list)) { errors.push('custom_attributes must be a list.'); return []; }
    const seen = new Set();
    return list.map((raw, i) => {
        const where = `custom_attributes[${i}]`;
        if (!isObject(raw)) { errors.push(`${where} must be an object.`); return raw; }
        const a = { ...raw };
        if (UI_ONLY_ATTRIBUTE_TYPES.includes(a.attribute_type)) {
            errors.push(`${where}: ${a.attribute_type} belongs to TANC, which is set up in Settings after creating.`);
            return a;
        }
        if (!(a.attribute_type in CUSTOM_ATTRIBUTE_TYPES)) {
            errors.push(`${where}: attribute_type must be one of ${Object.keys(CUSTOM_ATTRIBUTE_TYPES).join(', ')}.`);
            return a;
        }
        if (seen.has(a.attribute_type)) errors.push(`${where}: ${a.attribute_type} appears more than once.`);
        seen.add(a.attribute_type);
        if (a.sub_type === undefined) a.sub_type = 'FLEXICO';
        else if (a.sub_type !== 'FLEXICO') errors.push(`${where}: sub_type must be FLEXICO.`);
        Object.keys(a).filter((k) => !['attribute_type', 'attribute', 'sub_type'].includes(k)).forEach((k) => errors.push(`${where}: unknown field "${k}".`));

        if (a.attribute_type === 'GROUPING_PREFERENCE') {
            let pref = a.attribute;
            if (typeof pref === 'string') { try { pref = JSON.parse(pref); } catch (e) { pref = null; } }
            if (!isObject(pref) || !isObject(pref.group_info) || !isObject(pref.parameter_mapping_by_label)) {
                errors.push(`${where}: GROUPING_PREFERENCE needs { "group_info": {…}, "parameter_mapping_by_label": {…} }.`);
                return a;
            }
            Object.entries(pref.parameter_mapping_by_label).forEach(([label, group]) => {
                if (!parameterLabels.has(label)) errors.push(`${where}: parameter "${label}" is not in parameters.`);
                if (group !== 'ungrouped' && !(group in pref.group_info)) errors.push(`${where}: group "${group}" is not in group_info.`);
            });
            a.attribute = JSON.stringify(pref);
            return a;
        }
        if (a.attribute === undefined || a.attribute === null || String(a.attribute).trim() === '') {
            errors.push(`${where}: ${a.attribute_type} needs an attribute value.`);
            return a;
        }
        a.attribute = String(a.attribute);
        if (a.attribute_type === 'NOTIFICATION_PREFERENCE' && !(a.attribute in NOTIFICATION_PREFERENCES)) {
            errors.push(`${where}: NOTIFICATION_PREFERENCE must be one of ${Object.keys(NOTIFICATION_PREFERENCES).join(', ')}.`);
        }
        return a;
    });
};

/**
 * Checks one template and returns { template, errors, warnings }. `template` is the
 * normalised version that will be sent (before the page-filled fields are added).
 */
export const validateTemplate = (raw, { triggerTypes }) => {
    const errors = [];
    const warnings = [];
    if (!isObject(raw)) return { template: raw, errors: ['Each template must be a JSON object.'], warnings };
    const t = withoutNulls(raw);

    Object.keys(t).filter((k) => k in AUTO_SET_TEMPLATE_KEYS).forEach((k) => {
        delete t[k];
        warnings.push(`"${k}" is ignored (${AUTO_SET_TEMPLATE_KEYS[k]}).`);
    });
    Object.keys(t).filter((k) => !TEMPLATE_KEYS.has(k)).forEach((k) => errors.push(`Unknown field "${k}".`));

    ['template_name', 'label', 'description', 'sox_evidence'].forEach((k) => {
        if (k in t && typeof t[k] !== 'string') errors.push(`${k} must be text.`);
        else if (typeof t[k] === 'string') t[k] = t[k].trim();
    });
    if (isBlank(t.template_name)) errors.push('template_name is required.');
    if (isBlank(t.trigger_type)) errors.push('trigger_type is required.');
    else if (!triggerTypes.includes(t.trigger_type)) errors.push(`trigger_type must be one of: ${triggerTypes.join(', ')}.`);
    BOOLEAN_TEMPLATE_KEYS.forEach((k) => { if (k in t && typeof t[k] !== 'boolean') errors.push(`${k} must be true or false.`); });

    if (t.upload_mandatory && t.trigger_type !== UPLOAD_WITH_PARAMETERS) {
        delete t.upload_mandatory;
        warnings.push(`upload_mandatory only applies to "${UPLOAD_WITH_PARAMETERS}" and was ignored.`);
    }
    if ('uploaders' in t) t.uploaders = checkPeople(t.uploaders, 'uploaders', UPLOADER_TYPE_VALUES, errors);
    if ('approvers' in t) {
        t.approvers = checkPeople(t.approvers, 'approvers', APPROVER_TYPE_VALUES, errors).map((a, i) => {
            if (!isObject(a)) return a;
            if (a.column_index === undefined || a.column_index === null) return { ...a, column_index: 0 };
            if (!Number.isInteger(a.column_index) || a.column_index < 0) errors.push(`approvers[${i}] column_index must be a whole number from 0.`);
            return a;
        });
    }

    // Same combinations the form enforces.
    if (NO_APPROVAL_TRIGGER_TYPES.includes(t.trigger_type) && (t.sox_approved || t.approval_required)) {
        t.sox_approved = false;
        t.approval_required = false;
        t.approvers = [];
        warnings.push(`"${t.trigger_type}" does not support approvals; approval_required, sox_approved and approvers were cleared.`);
    }
    if (t.sox_approved && UPLOAD_TRIGGER_TYPES.includes(t.trigger_type) && !t.approval_required) {
        t.approval_required = true;
        warnings.push('approval_required was turned on, as the form does for 2 touch approval on upload input types.');
    }
    const approvers = Array.isArray(t.approvers) ? t.approvers : [];
    if (t.approval_required && approvers.length === 0) errors.push('approval_required needs at least one approver.');
    if (t.sox_approved && approvers.length < 2 && !approvers.some((a) => a?.type === 'APPROVER_LDAP')) {
        errors.push('2 touch approval (sox_approved) needs at least 2 approvers, or 1 APPROVER_LDAP group.');
    }
    if (approvers.length > 0 && !t.approval_required && !t.sox_approved) warnings.push('approvers are only used when approval_required is true.');
    if (t.sox_evidence && !t.sox_approved) warnings.push('sox_evidence is only used when sox_approved is true.');

    if (!Array.isArray(t.parameters)) {
        errors.push(`parameters must be a list and include ${CONTICO_JOB_LABEL}.`);
        t.parameters = [];
    } else {
        t.parameters = t.parameters.map((p, i) => normaliseParameter(p, i, errors, warnings));
    }
    const labels = new Set();
    t.parameters.forEach((p) => {
        if (!p?.label) return;
        if (labels.has(p.label)) errors.push(`Parameter label "${p.label}" is used more than once.`);
        labels.add(p.label);
    });
    const conticoJob = t.parameters.find((p) => p?.label === CONTICO_JOB_LABEL);
    if (!conticoJob) errors.push(`parameters must include ${CONTICO_JOB_LABEL} (fixed_inputs, the Glue job to run).`);
    else if (conticoJob.field_type !== 'fixed_inputs' || conticoJob.field_values?.length !== 1 || isBlank(conticoJob.field_values[0])) {
        errors.push(`${CONTICO_JOB_LABEL} must be fixed_inputs with exactly one value, the Glue job name.`);
    } else {
        // Trim it as the form does, so the Glue job that is checked is the one that is saved and run.
        conticoJob.field_values = [conticoJob.field_values[0].trim()];
    }

    if ('custom_attributes' in t) t.custom_attributes = normaliseCustomAttributes(t.custom_attributes, labels, errors, warnings);
    return { template: t, errors, warnings };
};

/** Validates every template and the names across the file. */
export const validateTemplates = (rawTemplates, { triggerTypes }) => {
    const results = rawTemplates.map((raw) => validateTemplate(raw, { triggerTypes }));
    const counts = {};
    results.forEach(({ template }) => {
        const name = template?.template_name;
        if (typeof name === 'string' && name) counts[name] = (counts[name] || 0) + 1;
    });
    results.forEach((r) => {
        const name = r.template?.template_name;
        if (counts[name] > 1) r.errors.push(`template_name "${name}" appears more than once in this file.`);
    });
    return results;
};

/** Adds the Glue job check the form runs when contico_job is entered. */
export const checkConticoJobs = async (results, checkGlueJob) => {
    await Promise.all(results.map(async (r) => {
        const job = r.template?.parameters?.find((p) => p?.label === CONTICO_JOB_LABEL)?.field_values?.[0]?.trim();
        if (!job || r.errors.some((e) => e.includes(CONTICO_JOB_LABEL))) return;
        try {
            const exists = await checkGlueJob(job, r.template.critical_data ? 'true' : 'false');
            if (!exists) r.errors.push(`Glue job "${job}" (${CONTICO_JOB_LABEL}) was not found${r.template.critical_data ? ' in the critical data account' : ''}.`);
        } catch (e) {
            r.errors.push(`Could not check Glue job "${job}": ${e?.message || 'request failed'}.`);
        }
    }));
    return results;
};

/** The exact payload the form sends: the template plus the fields the page fills in. */
export const buildCreatePayload = (template, { appId, userId, currentStage }) => {
    const stepFunction = FLEXICO_STEP_FUNCTION_CONFIG.find((c) => c.environment === currentStage)?.arn;
    if (!stepFunction) throw new Error(`No FlexiCo Step Function is configured for stage "${currentStage}".`);
    if (!appId) throw new Error('No app selected.');
    return {
        ...template,
        app_id: appId,
        created_by: userId,
        last_updated_by: userId,
        step_function_name: stepFunction,
        status: FLEXICO_TEMPLATE_STATUS.TEMPLATE_CREATED,
        parameters: template.parameters || [],
    };
};
